/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License 2.0 which accompany this distribution.
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *   https://www.eclipse.org/legal/epl-2.0/
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../../JSG');
const FormatAttributes = require('../../attr/FormatAttributes');
const {
	getColIndexFromRef,
	getRefFromColIndex,
	getRowIndexFromRef,
	getRefFromRowIndex,
	getPosFromRef,
	getRefFromPos
} = require('../../model/utils');

const areEqual = (border1, border2) =>
	border1.color === border2.color && border1.style === border2.style && border1.width === border2.width;

const getBorderKeys = (at) => {
	const key = `${at}border`;
	return { color: `${key}color`, style: `${key}style`, width: `${key}width` };
};

const getBorderFormats = (at) => {
	const keys = getBorderKeys(at);
	return ({ formats = {} } = {}) => {
		const color = formats[keys.color];
		const style = formats[keys.style];
		const width = formats[keys.width];
		return color != null || style != null || width != null ? { color, style, width } : undefined;
	};
};
const getTopBorderFormats = getBorderFormats('top');
const getLeftBorderFormats = getBorderFormats('left');
const getBottomBorderFormats = getBorderFormats('bottom');
const getRightBorderFormats = getBorderFormats('right');

const isVisibleBorder = (fmt) => fmt && fmt.style != null && fmt.style !== FormatAttributes.LineStyle.NONE;
const getVisibleBorder = (formats) => (formats && isVisibleBorder(formats) ? formats : undefined);
const getValue = (value, defValue) => (value != null ? value : defValue);
const combineFormats = (oldfmt, newfmt) => {
	if (oldfmt || newfmt) {
		oldfmt = oldfmt || {};
		newfmt = newfmt || {};
		return {
			color: getValue(newfmt.color, oldfmt.color),
			style: getValue(newfmt.style, oldfmt.style),
			width: getValue(newfmt.width, oldfmt.width)
		};
	}
	return undefined;
};

const getTopBorder = (cell, fmt) =>
	cell ? getVisibleBorder(combineFormats(getTopBorderFormats(cell.properties), fmt)) : fmt;
const getLeftBorder = (cell, fmt) =>
	cell ? getVisibleBorder(combineFormats(getLeftBorderFormats(cell.properties), fmt)) : fmt;
const getBottomBorder = (cell, fmt) =>
	cell ? getVisibleBorder(combineFormats(getBottomBorderFormats(cell.properties), fmt)) : fmt;
const getRightBorder = (cell, fmt) =>
	cell ? getVisibleBorder(combineFormats(getRightBorderFormats(cell.properties), fmt)) : fmt;

const hasPos = (list) => (x, y) => list.some((pos) => pos.x === x && pos.y === y);

const getBorderClearFormats = (at) => {
	const keys = getBorderKeys(at);
	return { [keys.color]: JSG.theme.border, [keys.style]: FormatAttributes.LineStyle.NONE, [keys.width]: 1 };
};

const getBorderProperties = (properties) => ({
	top: getTopBorderFormats(properties),
	left: getLeftBorderFormats(properties),
	bottom: getBottomBorderFormats(properties),
	right: getRightBorderFormats(properties)
});

const cellProvider = (sheet) => {
	const posFromRef = (cell) => getPosFromRef(cell.ref);
	const refFromPos = (x, y) => getRefFromPos(x, y, sheet);
	return {
		posFromRef,
		refFromPos,
		getAt: (x, y) => sheet.cells.getRC(x, y)
	};
};
const rowProvider = (sheet) => {
	const posFromRef = (row) => ({ y: getRowIndexFromRef(row.ref) });
	const refFromPos = (x, y) => getRefFromRowIndex(y);
	return {
		posFromRef,
		refFromPos,
		getAt: (x, y) => sheet.rows.getSectionAt(y)
	};
};
const columnProvider = (sheet) => {
	const posFromRef = (col) => ({ x: getColIndexFromRef(col.ref) });
	const refFromPos = (x, y) => getRefFromColIndex(x, sheet);
	return {
		posFromRef,
		refFromPos,
		getAt: (x, y) => sheet.columns.getSectionAt(x)
	};
};
const createProvider = (providerFn, sheet, refs) => {
	const provider = providerFn(sheet);
	const positions = refs.map(provider.posFromRef);
	return { refs, positions, ...provider };
};

const neighborBorderType = {
	top: 'bottom',
	left: 'right',
	right: 'left',
	bottom: 'top'
}
const getBorderFormatAt = {
	top: getTopBorder,
	left: getLeftBorder,
	right: getRightBorder,
	bottom: getBottomBorder
}
const getBorderFormat = (type, obj, newProps) => {
	const get = getBorderFormatAt[type];
	return get ? get(obj, newProps) : undefined;
};

const checkNeighborBorder = (type, obj, neighbor, newProps) => {
	const border = getBorderFormat(type, obj, newProps);
	const neighborType = neighborBorderType[type];
	const neighborBorder = getBorderFormat(neighborType, neighbor);
	return border && neighborBorder && !areEqual(border, neighborBorder)
		? getBorderClearFormats(neighborType)
		: undefined;
};
const checkNeighbor = (clearObjs, provider) => {
	const containsPos = hasPos(provider.positions);
	return (type, x, y, pivot, newprops) => {
		const neighbor = provider.getAt(x, y);
		const clearFormats = checkNeighborBorder(type, pivot, neighbor, newprops);
		if (clearFormats) {
			// const formats = containsPos(x, y) ? props.formats || {} : {};
			const formats = containsPos(x, y) ? neighbor.format || {} : {};
			const clearObj = {
				ref: provider.refFromPos(x, y),
				properties: { formats: { ...formats, ...clearFormats } }
			};
			if (clearObj) clearObjs.push(clearObj);
		}
	};
};
const checkBorder = (sheet, provider) => {
	const clearRefs = [];
	const check = checkNeighbor(clearRefs, provider);
	provider.refs.forEach((ref) => {
		const pos = provider.posFromRef(ref);
		const props = ref.properties || {};
		const pivot = provider.getAt(pos.x, pos.y);
		const { top, left, bottom, right } = getBorderProperties(props);
		if (isVisibleBorder(top) && pos.y - 1 > 0) check('top', pos.x, pos.y - 1, pivot, top);
		if (isVisibleBorder(left) && pos.x - 1 > -1) check('left', pos.x - 1, pos.y, pivot, left);
		if (isVisibleBorder(bottom) && pos.y + 1 < sheet.maxRows) check('bottom', pos.x, pos.y + 1, pivot, bottom);
		if (isVisibleBorder(right) && pos.x + 1 < sheet.maxColumns) check('right', pos.x + 1, pos.y, pivot, right);
	});
	return clearRefs;
};
const checkBorderFormats = (sheet, refs) => {
	const cellsProvider = createProvider(cellProvider, sheet, refs.cells);
	const rowsProvider = createProvider(rowProvider, sheet, refs.rows);
	const columnsProvider = createProvider(columnProvider, sheet, refs.cols);
	return {
		cells: checkBorder(sheet, cellsProvider),
		cols: checkBorder(sheet, columnsProvider),
		rows: checkBorder(sheet, rowsProvider)
	};
};

module.exports = {
	checkBorderFormats
};
